import { logger } from 'cadenza/utils/logging';
import { ajax } from 'cadenza/api-client/api';
import { cadenzaUrl } from 'cadenza/utils/cadenza-url/cadenza-url';
import { MapProperty } from 'cadenza/map/map-property';
import { context } from 'cadenza/map/context/context';

const GEO_REFERENCED_IMAGE_RESOLUTION_IN_DPI = 200;

export function showPrintMapDialog (map) {
  const mapId = map.get(MapProperty.ID);
  const mapSize = map.getInnerSize();
  Promise.all([
    import(/* webpackChunkName: "print-map-dialog" */ 'cadenza/map/print-dialog/print-map-dialog'),
    ajax(`/maps/${mapId}/print/options?mapSize=${mapSize}`),
    /* ensure the map size is properly initialized on the server as in classic the map is stateful
     * and starts with an uninitialized size,
     * avoid the throttling explicitly to avoid cases where the map print dialog is opened right
     * after opening the map before the PUSH has a chance to come through
     * or be in mercy of a race condition */
    context(map).pushUnthrottled(),
    preparePrint(map)
  ]).then(([ { PrintMapDialog }, options ]) => {
    const dialog = new PrintMapDialog(options,
      model => createPrintUrl(mapId, model),
      format => createExportGeoReferencedUrl(mapId, format),
      template => printPreviewImageSource(mapId, template),
      cancelId => geoReferencedExportPreviewImageSource(mapId, cancelId)
    );
    document.body.append(dialog);
    dialog.addEventListener('close', () => dialog.remove());
    dialog.fullscreen = true;
    dialog.show();
  }).catch(error => logger.error('Failed to show print map dialog.', error));
}

function preparePrint (map) {
  const allPreparePrintPromises = context(map).layers
    .filter(layer => layer.preparePrint)
    .map(layer => layer.preparePrint());
  return Promise.all(allPreparePrintPromises);
}

function createPrintUrl (mapId, { resolution, format, template, userInput }) {
  const path =   cadenzaUrl(`/maps/${mapId}/print/export`);
  const searchParams = new URLSearchParams({
    resolution,
    format,
    template
  });
  if (userInput) {
    Object.entries(userInput).forEach(([ name, value ], i) => {
      searchParams.append('paramName', name);
      searchParams.append('paramValue', value);
    });
  }
  return path + '?' + searchParams;
}

function createExportGeoReferencedUrl (mapId, format) {
  const params = new URLSearchParams({
    resolution: GEO_REFERENCED_IMAGE_RESOLUTION_IN_DPI,
    format
  });
  return cadenzaUrl(`/maps/${mapId}/geoImage/export?${params}`);
}

function printPreviewImageSource (mapId, template) {
  const path = cadenzaUrl(`/maps/${mapId}/print/preview`);
  return previewImageSource(path, { template });
}

function geoReferencedExportPreviewImageSource (mapId, cancelId) {
  const path = cadenzaUrl(`/maps/${mapId}/geoImage/preview`);
  return previewImageSource(path, { cancelId });
}

function previewImageSource (path, parameters) {
  const searchParams = new URLSearchParams(parameters);
  // Firefox does not refresh the image if the
  // new src is the same. So we add some dummy param:
  searchParams.append('_', Date.now());
  return path + '?' + searchParams;
}
